import React, { useCallback, useEffect, useState } from "react";
import { MediaItem, MediaStatus } from "./Media.types";
import MediaGrid from "./components/MediaGrid";
import MediaTabs from "./components/MediaTabs";
import MediaHeader from "./components/MediaHeader";
import MediaPreviewModal from "./components/MediaPreviewModal";
import { AdminServices } from "../../../services/admin/AdminServices";
import { Spinner } from "../../../components/Spinner/Spinner.component";
import Pagination from "./components/Pagination";
import toast from "react-hot-toast";
import { auth } from "../../../config/firebase";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { setCategories, setSubcategories } from "../../../store/referenceSlice";

type PendingAction = "VERIFY" | "REJECT" | null;

const Media: React.FC = () => {
  const dispatch = useDispatch();
  const categoriesByCode = useSelector(
    (state: RootState) => state.reference.categoriesByCode,
  );
  const subcategoriesByCode = useSelector(
    (state: RootState) => state.reference.subcategoriesByCode,
  );
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [pageState, setPageState] = useState({
    currentPage: 0,
    totalPages: null,
  });
  const [activeTab, setActiveTab] = useState<MediaStatus | "ALL">("VERIFICATION_PENDING");
  const [loading, setLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRejectReason, setSelectedRejectReason] = useState<any>(null);

  const fetchMedia = useCallback(
    async (status: MediaStatus | "ALL", page: number, signal?: AbortSignal) => {
      try {
        setLoading(true);
        setMediaList([]);

        const { response } = await AdminServices.getMediaList({
          status,
          page,
          signal,
        });
        setMediaList(response?.mediaList || []);
        setPageState((prev) => ({
          ...prev,
          totalPages: response?.totalPages || null,
        }));
      } catch (err: any) {
        if (err.name === "CanceledError" || err.name === "AbortError") {
          return;
        }
        console.error("Error fetching media list:", err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const handleConfirmAction = async () => {
  if (!selectedMedia || !auth.currentUser || !pendingAction) return;

  const payload = {
    id: selectedMedia.id,
    reviewedBy: auth.currentUser.uid,
    status: pendingAction === "VERIFY" ? "VERIFIED" : "REJECTED",
    reason:
      pendingAction === "REJECT"
        ? selectedRejectReason?.value ?? null
        : null,
    mediaLength: selectedMedia.length,
  };

  try {
    await AdminServices.reviewMedia(payload);

    toast.success(
      pendingAction === "VERIFY"
        ? "Media verified successfully"
        : "Media rejected successfully"
    );

    fetchMedia(activeTab, 0);
  } catch (error: any) {
    console.error("Failed to review media:", error);

    toast.error(
      error?.response?.data?.message ||
        "Something went wrong. Please try again."
    );
  } finally {
    setPendingAction(null);
    setSelectedMedia(null);
    setShowRejectModal(false);
    setSelectedRejectReason(null);
  }
};


  useEffect(() => {
    if (
      Object.keys(categoriesByCode).length > 0 &&
      Object.keys(subcategoriesByCode).length > 0
    ) {
      return;
    }

    const fetchCategoriesAndSubcategories = async () => {
      try {
        const categoriesData = await AdminServices.fetchCategories();
        if (Array.isArray(categoriesData?.response)) {
          dispatch(setCategories(categoriesData.response));
        }

        const subCategoriesData = await AdminServices.fetchSubcategories();
        if (Array.isArray(subCategoriesData?.response)) {
          dispatch(setSubcategories(subCategoriesData.response));
        }
      } catch (error) {
        console.error("Error fetching reference data:", error);
      }
    };

    fetchCategoriesAndSubcategories();
  }, [dispatch]);

  useEffect(() => {
    const controller = new AbortController();
    fetchMedia(activeTab, pageState.currentPage, controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchMedia, activeTab, pageState.currentPage]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <MediaHeader />

      <MediaTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        setPageState={setPageState}
      />

      {loading ? (
        <Spinner />
      ) : (
        <MediaGrid mediaList={mediaList} onViewMedia={setSelectedMedia} />
      )}

      {selectedMedia && (
        <MediaPreviewModal
          item={selectedMedia}
          onClose={() => setSelectedMedia(null)}
          pendingAction={pendingAction}
          setPendingAction={setPendingAction}
          handleConfirmAction={handleConfirmAction}
          showRejectModal={showRejectModal}
          setShowRejectModal={setShowRejectModal}
          selectedRejectReason={selectedRejectReason}
          setSelectedRejectReason={setSelectedRejectReason}
        />
      )}

      {mediaList.length>0 && pageState.totalPages && pageState.totalPages > 1 && !loading && (
        <Pagination
          currentPage={pageState.currentPage}
          totalPages={pageState.totalPages}
          onPageChange={(page) =>
            setPageState((p) => ({ ...p, currentPage: page }))
          }
        />
      )}
    </div>
  );
};

export default Media;
